import React, { useState, useCallback, useRef, useEffect } from 'react';
import './App.css';
import './dark-mode.css';
import AppHeader from './components/Header';
import BottomNavigation from './components/BottomNavigation';
import HomeView from './components/HomeView';
import DiscoverView from './components/DiscoverView';
// WhalesView entfernt aus Tab-Routing — Daten (/api/whale-trades) bleiben aktiv und werden von AssetDetail genutzt.
import EarningsCalendarView from './components/EarningsCalendarView';
import PortfolioView from './components/PortfolioView';
import SettingsView from './components/SettingsView';
import { useLivePrices } from './hooks/useLivePrices';
import { useDiscoverFeed } from './hooks/useDiscoverFeed';
import { useSignalAlerts } from './hooks/useSignalAlerts';
import { usePullToRefresh } from './hooks/usePullToRefresh';
import { useTheme } from './hooks/useTheme';
import { isNative } from './config/apiConfig';
import { initPush, syncWatchlistWithServer } from './services/pushService';
import OnboardingView from './components/OnboardingView';
import PaywallView from './components/PaywallView';
import PremiumBanner from './components/PremiumBanner';
import AnalyseView from './components/AnalyseView';
import { initRevenueCat } from './services/revenuecat';
import { useFreemium } from './hooks/useFreemium';
import { setItem, getItem, migrateToICloud, syncStore } from './services/iCloudStorage';

const PORTFOLIO_KEY   = 'finanzapp_portfolio_v1';
const ONBOARDING_KEY  = 'bullivo_onboarding_done';
const USER_NAME_KEY   = 'bullivo_user_name';

// Cookie helpers — survive WKWebView localStorage purges
function setCookie(key, val) {
  document.cookie = `${key}=${val};path=/;max-age=315360000;SameSite=Lax`; // 10 years
}
function getCookie(key) {
  const m = document.cookie.match(new RegExp(`(?:^|; )${key}=([^;]*)`));
  return m ? m[1] : null;
}

// Reset rebound mode on every true app start — prevents locked state after subscription cancellation
try { localStorage.setItem('bullivo_rebound_mode', '0'); } catch {}

function App() {
  const [onboardingDone, setOnboardingDone] = useState(() => {
    try {
      return !!localStorage.getItem(ONBOARDING_KEY) || getCookie(ONBOARDING_KEY) === '1';
    } catch { return getCookie(ONBOARDING_KEY) === '1'; }
  });
  const [appReady, setAppReady] = useState(false);

  const [showPaywall, setShowPaywall]   = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showNamePrompt, setShowNamePrompt] = useState(false);
  const [nameInput, setNameInput]       = useState('');
  const [userName, setUserName]         = useState(() => {
    try { return localStorage.getItem(USER_NAME_KEY) || null; } catch { return null; }
  });
  const { activatePremium, getLimit, isPremium, freemiumActive } = useFreemium();

  // RevenueCat init (anonymous, uses Apple ID internally)
  useEffect(() => { initRevenueCat(null); }, []);

  // iCloud: migrate localStorage → iCloud on first native launch, then sync
  useEffect(() => {
    (async () => {
      await migrateToICloud();
      await syncStore();
      // Load iCloud values that may differ from localStorage
      const cloudName = await getItem(USER_NAME_KEY);
      if (cloudName && cloudName !== userName) setUserName(cloudName);
      const cloudOnboarding = await getItem(ONBOARDING_KEY);
      if (cloudOnboarding) {
        setOnboardingDone(true);
        setCookie(ONBOARDING_KEY, '1'); // Persist to cookie for resilience
      }
      // Restore watchlist from iCloud if localStorage is empty (WKWebView purge recovery)
      const localPortfolio = localStorage.getItem(PORTFOLIO_KEY);
      if (!localPortfolio || localPortfolio === '[]') {
        const cloudPortfolio = await getItem(PORTFOLIO_KEY);
        if (cloudPortfolio) {
          try {
            const parsed = JSON.parse(cloudPortfolio);
            if (Array.isArray(parsed) && parsed.length > 0) {
              localStorage.setItem(PORTFOLIO_KEY, cloudPortfolio);
              setPortfolioVersion(v => v + 1);
            }
          } catch {}
        }
      }
      setAppReady(true);
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // iCloud sync on app resume
  useEffect(() => {
    const handleVisibilityCloud = () => {
      if (document.visibilityState === 'visible') syncStore();
    };
    document.addEventListener('visibilitychange', handleVisibilityCloud);
    return () => document.removeEventListener('visibilitychange', handleVisibilityCloud);
  }, []);

  // Nach Onboarding: Name abfragen wenn nicht gesetzt
  useEffect(() => {
    if (onboardingDone && !userName) setShowNamePrompt(true);
  }, [onboardingDone, userName]);

  const handleSaveName = () => {
    const name = nameInput.trim();
    if (!name) { setShowNamePrompt(false); return; }
    try { localStorage.setItem(USER_NAME_KEY, name); } catch {}
    setItem(USER_NAME_KEY, name).catch(() => {});
    setUserName(name);
    setShowNamePrompt(false);
  };

  const handleOnboardingComplete = useCallback(() => {
    // 3 unabhängige Writes — jeder mit eigenem try-catch, keiner blockiert die anderen
    try { localStorage.setItem(ONBOARDING_KEY, '1'); } catch {}
    setCookie(ONBOARDING_KEY, '1');
    setItem(ONBOARDING_KEY, '1').catch(() => {}); // async native (UserDefaults + iCloud)
    setOnboardingDone(true);
  }, []);

  const [activeTab, setActiveTab] = useState('home');
  const [pushOpenTicker, setPushOpenTicker] = useState(null);
  const [portfolioVersion, setPortfolioVersion] = useState(0);
  const [watchlistAddTrigger, setWatchlistAddTrigger] = useState(0);
  const contentRef      = useRef(null);
  const closeDetailRef  = useRef(null);

  // ── Settings swipe-to-dismiss ──
  const settingsSheetRef = useRef(null);
  const settingsDragY    = useRef(0);
  const settingsStartY   = useRef(0);
  const settingsDragging = useRef(false);
  const settingsIntent   = useRef(false);

  const onSettingsTouchStart = useCallback((e) => {
    settingsStartY.current  = e.touches[0].clientY;
    settingsDragY.current   = 0;
    settingsDragging.current = false;
    settingsIntent.current  = false;
  }, []);

  const onSettingsTouchMove = useCallback((e) => {
    const sheet = settingsSheetRef.current;
    if (!sheet) return;
    const scrollEl = sheet.querySelector('.sv-wrap');
    const dy = e.touches[0].clientY - settingsStartY.current;
    if (!settingsIntent.current) {
      settingsIntent.current = true;
      // Allow drag-down when scrolled to top OR when touch started near the handle area (top 60px)
      const startedNearTop = settingsStartY.current < sheet.getBoundingClientRect().top + 60;
      if (((!scrollEl || scrollEl.scrollTop <= 0) || startedNearTop) && dy > 6) {
        settingsDragging.current = true;
      } else {
        settingsDragging.current = false;
        return;
      }
    }
    if (!settingsDragging.current || dy <= 0) return;
    settingsDragY.current = dy;
    sheet.style.transform = `translateY(${dy}px)`;
    sheet.style.transition = 'none';
  }, []);

  const onSettingsTouchEnd = useCallback(() => {
    if (!settingsDragging.current) return;
    settingsDragging.current = false;
    settingsIntent.current = false;
    const sheet = settingsSheetRef.current;
    if (settingsDragY.current > 120) {
      if (sheet) {
        sheet.style.transition = 'transform 0.25s ease-out';
        sheet.style.transform = 'translateY(100%)';
      }
      setTimeout(() => setShowSettings(false), 200);
    } else {
      if (sheet) {
        sheet.style.transition = 'transform 0.2s ease-out';
        sheet.style.transform = 'translateY(0)';
      }
    }
  }, []);

  const registerDetailCloser = useCallback((fn) => {
    closeDetailRef.current = fn;
  }, []);

  const handleTabChange = useCallback((tab) => {
    if (tab === activeTab && closeDetailRef.current) {
      closeDetailRef.current();
      return;
    }
    setActiveTab(tab);
    setItem('bullivo_tab', tab);
  }, [activeTab]);

  const { assets, loading: pricesLoading, error, addTicker, removeTicker, refresh: refreshPrices } = useLivePrices();
  const { assets: discoverAssets, loading: discoverLoading, error: discoverError, lastUpdated, refresh: refreshDiscover } = useDiscoverFeed();
  const { setAlert } = useSignalAlerts();

  const [analyseRefreshTrigger, setAnalyseRefreshTrigger] = useState(0);

  const handlePullRefresh = useCallback(async () => {
    if (activeTab === 'analyse') setAnalyseRefreshTrigger((n) => n + 1);
    else if (activeTab === 'signale' || activeTab === 'home') await refreshDiscover();
    else await refreshPrices();
  }, [activeTab, refreshDiscover, refreshPrices]);

  const { pullY, refreshing } = usePullToRefresh(contentRef, handlePullRefresh);
  const { theme, setTheme } = useTheme();

  // Push-Notifications initialisieren
  useEffect(() => { initPush(); }, []);

  // Earnings-Calendar Prefetch beim App-Start (damit Termine-Tab sofort gefüllt ist)
  useEffect(() => {
    (async () => {
      try {
        const { PROXY_BASE } = await import('./config/apiConfig');
        const { getAuthHeaders } = await import('./services/authService');
        const r = await fetch(`${PROXY_BASE}/api/earnings-calendar`, {
          headers: { Accept: 'application/json', ...(await getAuthHeaders()) },
        });
        if (!r.ok) return;
        const data = await r.json();
        const list = Array.isArray(data?.events) ? data.events : [];
        if (list.length > 0) {
          localStorage.setItem('bullivo_termine_cache_v1', JSON.stringify({ ts: Date.now(), events: list }));
        }
      } catch { /* silent */ }
    })();
  }, []);

  // Notification-Tap: Watchlist-Signal → Watchlist + Asset öffnen; alles andere → Home
  useEffect(() => {
    const handler = (e) => {
      const { ticker, type } = e.detail || {};
      if (type === 'watchlist-signal' && ticker) {
        setActiveTab('watchlist');
        setItem('bullivo_tab', 'watchlist');
        setPushOpenTicker(ticker);
      } else {
        setActiveTab('home');
        setItem('bullivo_tab', 'home');
      }
    };
    window.addEventListener('push-navigate', handler);
    return () => window.removeEventListener('push-navigate', handler);
  }, []);

  // Nach längerer Zeit im Hintergrund (>5 Min) → Home zeigen
  useEffect(() => {
    let bgTimestamp = null;
    const BG_TIMEOUT_MS = 5 * 60 * 1000;
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        bgTimestamp = Date.now();
      } else if (document.visibilityState === 'visible' && bgTimestamp !== null) {
        if (Date.now() - bgTimestamp >= BG_TIMEOUT_MS) {
          setActiveTab('home');
          setItem('bullivo_tab', 'home');
        }
        bgTimestamp = null;
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  // Watchlist mit Server synchen – nur persönliche Portfolio-Ticker, nicht die Default-Liste
  useEffect(() => {
    try {
      const portfolio = JSON.parse(localStorage.getItem(PORTFOLIO_KEY) || '[]');
      const portfolioTickers = portfolio.map(p => p.ticker);
      if (portfolioTickers.length === 0) return;
      const personalAssets = assets.filter(a => portfolioTickers.includes(a.ticker));
      if (personalAssets.length === 0) return;
      const alertsEnabled = (() => {
        try {
          const v = localStorage.getItem('bullivo_realtime_alerts');
          return v === null ? true : v === 'true';
        } catch { return true; }
      })();
      syncWatchlistWithServer(personalAssets, alertsEnabled);
    } catch { /* ignore */ }
  }, [assets]);

  const addToPortfolio = useCallback(async (asset) => {
    try {
      const current = JSON.parse(localStorage.getItem(PORTFOLIO_KEY) || '[]');
      if (current.find(p => p.ticker === asset.ticker)) return;
      const entry = {
        ticker:   asset.ticker,
        name:     asset.name,
        type:     asset.type || 'stock',
        buyPrice: asset.price ?? null,
        buyDate:  new Date().toISOString(),
      };
      const updated = JSON.stringify([...current, entry]);
      localStorage.setItem(PORTFOLIO_KEY, updated);
      setItem(PORTFOLIO_KEY, updated);
    } catch { /* ignore */ }

    addTicker(asset.ticker, asset.name, asset.type);
    await setAlert(asset.ticker, 'sell', true);
    setPortfolioVersion(v => v + 1);
  }, [addTicker, setAlert]);

  const indicatorH = refreshing ? 44 : Math.min(pullY, 44);

  // Wait for iCloud check before deciding — localStorage can be wiped on iOS force-close
  if (!appReady && !onboardingDone) {
    return null; // Brief blank screen while iCloud loads (~50ms)
  }

  if (!onboardingDone) {
    return <OnboardingView onComplete={handleOnboardingComplete} />;
  }

  return (
    <div className={`App${isNative() ? ' App--native' : ''}`}>
      <AppHeader
        activeTab={activeTab}
        onOpenSettings={() => setShowSettings(true)}
        onOpenWatchlistSearch={() => setWatchlistAddTrigger(v => v + 1)}
      />

      <main className="App-content" ref={contentRef}>

        {/* Pull-to-Refresh Indikator */}
        <div
          className="ptr-bar"
          style={{
            height: indicatorH,
            transition: pullY > 0 ? 'none' : 'height 0.25s ease',
          }}
        >
          {refreshing ? (
            <span className="ptr-spinner" />
          ) : pullY > 12 ? (
            <span
              className="ptr-arrow"
              style={{
                transform: `rotate(${Math.min(pullY / 65, 1) * 180}deg)`,
                opacity: Math.min(pullY / 30, 1),
              }}
            >↓</span>
          ) : null}
        </div>

        {error && (
          <div className="offline-banner">
            Keine Internetverbindung
          </div>
        )}

        {activeTab === 'home' && (
          <HomeView
            assets={discoverAssets}
            loading={discoverLoading}
            userName={userName}
            onAddToPortfolio={addToPortfolio}
            onTabChange={handleTabChange}
            onRegisterDetailCloser={registerDetailCloser}
          />
        )}

        {activeTab === 'signale' && (
          <DiscoverView
            assets={discoverAssets}
            loading={discoverLoading}
            error={discoverError}
            lastUpdated={lastUpdated}
            refresh={refreshDiscover}
            onAddToPortfolio={addToPortfolio}
            onRegisterDetailCloser={registerDetailCloser}
            onShowPaywall={() => setShowPaywall(true)}
          />
        )}

        {activeTab === 'earnings' && (
          <EarningsCalendarView
            assets={discoverAssets}
            onRegisterDetailCloser={registerDetailCloser}
          />
        )}

        {activeTab === 'watchlist' && (
          <PortfolioView
            assets={assets.map(a => {
              const enriched = discoverAssets.find(d => d.ticker === a.ticker);
              return enriched ? { ...a, ...enriched, price: a.price, change: a.change } : a;
            })}
            addTicker={addTicker}
            removeTicker={removeTicker}
            portfolioVersion={portfolioVersion}
            onRegisterDetailCloser={registerDetailCloser}
            openTicker={pushOpenTicker}
            onOpenTickerHandled={() => setPushOpenTicker(null)}
            onShowPaywall={() => setShowPaywall(true)}
            addTrigger={watchlistAddTrigger}
          />
        )}

        {activeTab === 'analyse' && (
          <AnalyseView assets={[...discoverAssets, ...assets]} onAddToPortfolio={addToPortfolio} getLimit={getLimit} onShowPaywall={() => setShowPaywall(true)} refreshTrigger={analyseRefreshTrigger} />
        )}

      </main>

      {showSettings && (
        <div className="settings-modal-overlay" onClick={() => setShowSettings(false)}>
          <div className="settings-modal-sheet" ref={settingsSheetRef}
            onClick={e => e.stopPropagation()}
            onTouchStart={onSettingsTouchStart}
            onTouchMove={onSettingsTouchMove}
            onTouchEnd={onSettingsTouchEnd}>
            <div className="settings-modal-handle" />
            <SettingsView
              theme={theme}
              setTheme={setTheme}
              userName={userName}
              onShowPaywall={() => { setShowSettings(false); setShowPaywall(true); }}
              assets={assets}
              onClose={() => setShowSettings(false)}
            />
          </div>
        </div>
      )}

      {freemiumActive && !isPremium && onboardingDone && (
        <PremiumBanner onUpgrade={() => setShowPaywall(true)} />
      )}

      {showPaywall && (
        <PaywallView
          onSuccess={() => { activatePremium(); setShowPaywall(false); }}
          onClose={() => setShowPaywall(false)}
        />
      )}

      {showNamePrompt && (
        <div className="name-prompt-overlay">
          <div className="name-prompt-card">
            <h3 className="name-prompt-title">Wie heißt du?</h3>
            <p className="name-prompt-sub">So personalisieren wir deine App.</p>
            <input
              className="name-prompt-input"
              type="text"
              placeholder="Dein Vorname"
              value={nameInput}
              onChange={e => setNameInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSaveName()}
              autoFocus
            />
            <button className="name-prompt-btn" onClick={handleSaveName}>
              Speichern
            </button>
            <button className="name-prompt-skip" onClick={() => setShowNamePrompt(false)}>
              Überspringen
            </button>
          </div>
        </div>
      )}

      <BottomNavigation activeTab={activeTab} onTabChange={handleTabChange} />
    </div>
  );
}

export default App;
